import { createApp } from "vue";

import { pt } from "./assets/locale/pt.json";

import "@/assets/styles.scss";
import App from "./components/App.vue";

import "primeflex/primeflex.min.css";
import "primeicons/primeicons.css";

import router from "@/routers/routerConfig";
import api from "@/stores/api.js";
import { definePreset } from "@primevue/themes";
import Aura from "@primevue/themes/aura";
import * as Sentry from "@sentry/vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import Button from "primevue/button";
import Card from "primevue/card";
import Chip from "primevue/chip";
import Column from "primevue/column";
import PrimeVue from "primevue/config";
import ConfirmationService from "primevue/confirmationservice";
import ConfirmPopup from "primevue/confirmpopup";
import DataTable from "primevue/datatable";
import Dialog from "primevue/dialog";
import Divider from "primevue/divider";
import Fieldset from "primevue/fieldset";
import FileUpload from "primevue/fileupload";
import Image from "primevue/image";
import InputGroup from "primevue/inputgroup";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import Menubar from "primevue/menubar";
import MultiSelect from "primevue/multiselect";
import ProgressSpinner from "primevue/progressspinner";
import RadioButton from "primevue/radiobutton";
import Ripple from "primevue/ripple";
import Select from "primevue/select";
import Skeleton from "primevue/skeleton";
import Tab from "primevue/tab";
import TabList from "primevue/tablist";
import TabPanel from "primevue/tabpanel";
import TabPanels from "primevue/tabpanels";
import Tabs from "primevue/tabs";
import Textarea from "primevue/textarea";

const isProductionEnvironment = import.meta.env.PROD;

const app = createApp(App);

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: "#fff8f5",
      100: "#fef1ea",
      200: "#fde0d0",
      300: "#fbc7a6",
      400: "#f8a572",
      500: "#f5631a",
      600: "#e84910",
      700: "#c23a0d",
      800: "#9d3013",
      900: "#7f2a14",
      950: "#451309",
    },

    success: {
      50: "#f0fdf5",
      100: "#dcfce8",
      200: "#bbf7d1",
      300: "#86efac",
      400: "#4ade80",
      500: "#00c27d",
      600: "#16a34a",
      700: "#15803d",
      800: "#166534",
      900: "#14532d",
    },

    danger: {
      50: "#fef2f2",
      100: "#fee2e2",
      200: "#fecaca",
      300: "#fca5a5",
      400: "#f87171",
      500: "#f83b3b",
      600: "#dc2626",
      700: "#b91c1c",
      800: "#991b1b",
      900: "#7f1d1d",
    },

    formField: {
      borderRadius: "8px",
      paddingX: "1rem",
      paddingY: "0.75rem",
    },

    colorScheme: {
      light: {
        surface: {
          0: "#ffffff",
          50: "#fafafa",
          100: "#f5f5f5",
          200: "#e5e5e5",
          300: "#d4d4d4",
          400: "#a3a3a3",
          500: "#737373",
          600: "#525252",
          700: "#404040",
          800: "#262626",
          900: "#171717",
          950: "#0a0a0a",
        },
      },
    },
  },

  components: {
    button: {
      borderRadius: "8px",
      paddingX: "1.25rem",
      paddingY: "0.75rem",
      sm: {
        fontSize: "0.875rem",
        paddingX: "1rem",
        paddingY: "0.5rem",
      },
      lg: {
        fontSize: "1.125rem",
        paddingX: "1.5rem",
        paddingY: "1rem",
      },
    },

    card: {
      borderRadius: "12px",
      shadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    },

    panel: {
      borderRadius: "10px",
    },

    menubar: {
      borderRadius: "0px",
      padding: "1rem 1.5rem",
    },

    inputtext: {
      borderRadius: "8px",
      paddingX: "1rem",
      paddingY: "0.75rem",
    },

    select: {
      borderRadius: "8px",
      paddingX: "1rem",
      paddingY: "0.75rem",
    },

    multiselect: {
      borderRadius: "8px",
      paddingX: "1rem",
      paddingY: "0.75rem",
    },

    datatable: {
      borderRadius: "10px",
    },

    dialog: {
      borderRadius: "12px",
      shadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    },

    fieldset: {
      borderRadius: "10px",
    },

    divider: {
      borderColor: "rgba(0, 0, 0, 0.08)",
    },
  },
});

app.use(PrimeVue, {
  ripple: true,
  locale: pt,
  theme: {
    preset: MyPreset,
    options: {
      darkModeSelector: "none",
      cssLayer: {
        name: "primevue",
        order: "tailwind-base, primevue, tailwind-utilities",
      },
    },
  },
});

app.use(router);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: isProductionEnvironment,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [/^https:\/\/saep-ia-hml1\.hml\.sc\.senai\.br/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const pinia = createPinia();

pinia.use(piniaPluginPersistedstate);

app.use(pinia);

app.provide("axios", api);

app.use(ConfirmationService);
app.component("Card", Card);
app.component("Button", Button);
app.component("Dialog", Dialog);
app.component("RadioButton", RadioButton);
app.component("DataTable", DataTable);
app.component("Column", Column);
app.component("InputText", InputText);
app.component("InputNumber", InputNumber);
app.component("Textarea", Textarea);
app.component("FileUpload", FileUpload);
app.component("InputGroup", InputGroup);
app.component("Skeleton", Skeleton);
app.component("Select", Select);
app.component("ProgressSpinner", ProgressSpinner);
app.component("MultiSelect", MultiSelect);
app.component("Tabs", Tabs);
app.component("Tab", Tab);
app.component("TabList", TabList);
app.component("TabPanels", TabPanels);
app.component("TabPanel", TabPanel);
app.component("ConfirmPopup", ConfirmPopup);
app.component("Fieldset", Fieldset);
app.component("Chip", Chip);
app.component("Divider", Divider);
app.component("Image", Image);
app.component("Menubar", Menubar);

app.directive("ripple", Ripple);

app.mount("#app");
